import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { MenuProps } from 'antd';
import { Dropdown, Modal } from 'antd';
import { MoreOutlined } from '@ant-design/icons';
import { DeleteOutlined, EditOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useDeleteContact } from 'api/contacts/use-delete-contact.ts';
import type { ContactRes } from 'api/swagger';
import { UserRole, useRoleGuard } from 'shared/hooks/use-role-guard.ts';
import type { TDropdownChangeInfo } from 'types/dropdown-change-info';
import { EditContact } from 'components/contacts/edit-contact';

export const ContactControl: React.FC<{ contact: ContactRes }> = ({ contact }) => {
  const { t } = useTranslation();

  const isAllow = useRoleGuard([UserRole.admin, UserRole.assistant, UserRole.customer_manager]);

  const [modal, contextHolder] = Modal.useModal();
  const [isActiveControl, setIsActiveControl] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const { mutateAsync: deleteContactApi } = useDeleteContact(contact.id);

  const deactivateWithTimeout = () => {
    setTimeout(() => setIsActiveControl(false), 300);
  };

  const confirm = () => {
    modal.confirm({
      title: t('common:confirm'),
      icon: <ExclamationCircleOutlined />,
      content: t('common:delete_confirm_text'),
      okText: t('common:delete'),
      cancelText: t('common:cancel'),
      onCancel: () => deactivateWithTimeout(),
      onOk: async () => {
        await deleteContactApi();
        deactivateWithTimeout();
      },
    });
  };

  const handleDropdownClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'edit') {
      setIsEditOpen(true);
    } else if (key === 'delete') {
      confirm();
    }
  };

  const handleCloseEdit = () => {
    setIsEditOpen(false);
    deactivateWithTimeout();
  };

  const handleDropdownOpenChange = (value: boolean, info: TDropdownChangeInfo) => {
    if (info.source === 'trigger') {
      setIsActiveControl(value);
    }
  };

  if (!isAllow) return null;

  return (
    <>
      <Dropdown
        key='more'
        trigger={['click']}
        menu={{
          items: [
            {
              key: 'edit',
              label: t('common:edit'),
              icon: <EditOutlined />,
            },
            {
              key: 'delete',
              label: t('common:delete'),
              icon: <DeleteOutlined />,
            },
          ],
          onClick: handleDropdownClick,
        }}
        placement='bottomLeft'
        onOpenChange={handleDropdownOpenChange}>
        <MoreOutlined style={{ fontSize: 20 }} />
      </Dropdown>
      {isActiveControl ? <EditContact contact={contact} isOpen={isEditOpen} onClose={handleCloseEdit} /> : null}
      {isActiveControl ? contextHolder : null}
    </>
  );
};
